import React from "react";
import axios from "axios";
import { motion, AnimatePresence } from "framer-motion";
import { Lock, Loader2, X } from "lucide-react";
import { toast } from "sonner";
import { FinanceCommandDashboard } from "./finance/FinanceCommandDashboard";
import { useFinanceState } from "@/hooks/useFinanceState";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const STORAGE_KEY = "hx_finance_token_v1";

const UnlockModal = ({ open, onClose, onUnlocked }) => {
  const [tok, setTok] = React.useState("");
  const [busy, setBusy] = React.useState(false);
  const inputRef = React.useRef(null);

  React.useEffect(() => {
    if (open) { setTok(""); setTimeout(() => inputRef.current?.focus(), 50); }
  }, [open]);

  const submit = async (e) => {
    e.preventDefault();
    if (!tok.trim()) return;
    setBusy(true);
    try {
      await axios.post(`${API}/finance/unlock`, { token: tok.trim() });
      localStorage.setItem(STORAGE_KEY, tok.trim());
      onUnlocked(tok.trim());
      onClose();
      toast.success("Painel privado desbloqueado.");
    } catch (err) {
      toast.error(err?.response?.status === 401 ? "Token inválido." : "Falha ao desbloquear.");
    } finally { setBusy(false); }
  };

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
          className="fixed inset-0 z-50 flex items-center justify-center p-5 bg-black/80 backdrop-blur-xl"
          onClick={onClose}
        >
          <motion.div
            initial={{ scale: 0.96, opacity: 0, y: 10 }}
            animate={{ scale: 1, opacity: 1, y: 0 }}
            exit={{ scale: 0.96, opacity: 0 }}
            transition={{ duration: 0.25, ease: [0.22, 1, 0.36, 1] }}
            onClick={(e) => e.stopPropagation()}
            className="relative w-full max-w-md rounded-2xl border border-white/[0.10] p-7 lg:p-9"
            style={{
              background: "linear-gradient(180deg, rgba(20,26,34,0.95) 0%, rgba(10,15,26,0.90) 100%)",
              boxShadow: "0 30px 80px -30px rgba(0,0,0,0.8), inset 0 0 0 1px rgba(10,155,95,0.06)",
            }}
            data-testid="finance-unlock-modal"
          >
            <button onClick={onClose} className="absolute top-3 right-3 p-1.5 rounded hover:bg-white/[0.03] text-hx-muted hover:text-hx-text transition-colors" aria-label="Fechar">
              <X size={14} />
            </button>
            <div className="w-10 h-10 rounded-md hx-metal flex items-center justify-center">
              <Lock size={16} className="text-hx-neon" strokeWidth={1.6} />
            </div>
            <h2 className="mt-5 font-display font-semibold text-2xl tracking-tight">Desbloquear painel privado</h2>
            <p className="mt-2 text-[13px] text-hx-muted leading-relaxed">
              Introduz o token pessoal. O acesso é guardado localmente no dispositivo até bloqueares manualmente.
            </p>
            <form onSubmit={submit} className="mt-6 flex flex-col gap-3">
              <input
                ref={inputRef}
                type="password"
                autoComplete="current-password"
                value={tok}
                onChange={(e) => setTok(e.target.value)}
                placeholder="Token"
                data-testid="finance-unlock-input"
                className="bg-black/40 border border-white/[0.10] focus:border-hx-neon/40 rounded-md px-3 py-2.5 text-[14px] text-hx-text outline-none transition-colors font-mono placeholder:text-hx-muted/40"
              />
              <button
                type="submit"
                disabled={busy || !tok.trim()}
                data-testid="finance-unlock-submit"
                className="inline-flex items-center justify-center gap-2 px-5 py-2.5 rounded-full bg-hx-neon text-hx-bg font-semibold text-[11px] uppercase tracking-[0.18em] hover:bg-hx-neon-soft transition-all disabled:opacity-50"
                style={{ boxShadow: "0 0 20px -2px rgba(10,155,95,0.5)" }}
              >
                {busy ? <Loader2 size={12} className="animate-spin" /> : <Lock size={11} strokeWidth={2.2} />}
                Desbloquear
              </button>
            </form>
            <p className="mt-5 text-[10px] uppercase tracking-[0.22em] text-hx-muted/70">
              Dados privados visíveis apenas ao operador autenticado.
            </p>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default function FinancePage() {
  const [token, setToken] = React.useState(() => {
    try { return (localStorage.getItem(STORAGE_KEY) || "").trim(); } catch { return ""; }
  });
  const [modalOpen, setModalOpen] = React.useState(false);

  // Verify saved token on mount — clear if invalid
  React.useEffect(() => {
    if (!token) return;
    let cancelled = false;
    axios.post(`${API}/finance/unlock`, { token })
      .catch(() => { if (!cancelled) { localStorage.removeItem(STORAGE_KEY); setToken(""); } });
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const lock = () => {
    localStorage.removeItem(STORAGE_KEY);
    setToken("");
    toast.success("Painel bloqueado.");
  };

  const state = useFinanceState(token);

  return (
    <>
      <FinanceCommandDashboard
        unlocked={!!token}
        onUnlockClick={() => setModalOpen(true)}
        onLock={lock}
        state={state}
      />
      <UnlockModal open={modalOpen} onClose={() => setModalOpen(false)} onUnlocked={setToken} />
    </>
  );
}
